// ConfigContext.js
import React, { createContext, useState, useEffect, useContext } from "react";
import Spinner from "./components/Spinner";
import DOMPurify from 'dompurify';

const ConfigContext = createContext();

const ConfigProvider = ({ children }) => {
  const [config, setConfig] = useState(null);
  const [configError, setConfigError] = useState(null);
  const [loading, setLoading] = useState(false);
  let loadingTimer;

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        loadingTimer = setTimeout(() => {
          setLoading(true);
        }, 250);
        const configData = {};
        configData.currencyFormatter = new Intl.NumberFormat("en-CA", {
          style: "currency",
          currency: "CAD",
        });
        configData.timeFormatter = new Intl.DateTimeFormat(undefined, {
          hour: "numeric",
          minute: "numeric",
          hour12: true,
        });
        configData.dateFormatter = new Intl.DateTimeFormat("en-CA", {
          year: "numeric",
          month: "long",
          day: "numeric",
        });
        configData.apiUrl =
          typeof drupalSettings !== "undefined"
            ? drupalSettings.apt_config.api_url
            : process.env.apiUrl;
        configData.apiKey =
          typeof drupalSettings !== "undefined"
            ? drupalSettings.apt_config.api_key
            : process.env.apiKey;
        configData.offlineMessage =
          typeof drupalSettings !== "undefined"
            ? drupalSettings.apt_config.offline_message
            : process.env.offlineMessage;

        setConfig(configData);
        // get metadata from Power Platform
        const response = await fetch(`${configData.apiUrl}/metadata`, {
          method: "GET",
          headers: {
            "Api-Key": configData.apiKey,
          },
        });
        if (!response.ok) {
          throw new Error("Failed to fetch metadata");
        }

        const jsonBody = await response.json();
        configData.globalOptions = jsonBody.globalOptions;
        configData.apt_MonerisEnvironment = jsonBody.apt_MonerisEnvironment;
        // user configuration data from apt_configuration
        jsonBody.apt_configuration.forEach(item => {
          configData[item.apt_name] = { ...item };
        });
        setConfig(configData);
        clearTimeout(loadingTimer);
        setLoading(false);
      } catch (error) {
        console.error(error.message);
        setConfigError(error.message);
      }
    };

    // Check if config is not already loaded before making the API call
    if (!config) {
      fetchConfig();
    }
  }, [config]);

  if (configError) {
    return (
      <div className="app-error" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(config?.offlineMessage ?? '<p>Our system is temporarily unavailable but will be back soon. Please call 1-888-448-0000 or email us at <a href="mailto:apt@york.ca">apt@york.ca</a> for assistance in the meantime.</p>') }}>
      </div>
    );
  }

  if (loading) {
    return <div><Spinner label="Loading ..."/></div>;
  } else if(!config){
    return <></>
  } else {
    return (
      <ConfigContext.Provider value={{ config, configError }}>
        {children}
      </ConfigContext.Provider>
    );
  }
};

const useConfig = () => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error("useConfig must be used within a ConfigProvider");
  }
  return context;
};

export { ConfigProvider, useConfig };
